import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Panel } from '@/components/ui/Panel';
import { num } from '@/lib/format';
import { portalEvents, portalPartner } from '@/mocks/portal';

export default function PortalHomePage() {
  const { t } = useTranslation();

  const actions = [
    {
      to: '/portal/orders',
      icon: 'ri-shopping-bag-3-line',
      title: t('portal.home.cardOrdersTitle', { count: 2 }),
      body: t('portal.home.cardOrdersBody'),
      cta: t('portal.home.cardOrdersCta'),
      tone: 'border-l-primary-500',
      iconTone: 'bg-primary-50 text-primary-700',
    },
    {
      to: '/portal/reservations',
      icon: 'ri-calendar-check-line',
      title: t('portal.home.cardResTitle', { count: 3 }),
      body: t('portal.home.cardResBody'),
      cta: t('portal.home.cardResCta'),
      tone: 'border-l-accent-500',
      iconTone: 'bg-accent-50 text-accent-700',
    },
    {
      to: '/portal/products',
      icon: 'ri-error-warning-line',
      title: t('portal.home.cardStockTitle'),
      body: t('portal.home.cardStockBody'),
      cta: t('portal.home.cardStockCta'),
      tone: 'border-l-rose-400',
      iconTone: 'bg-rose-50 text-rose-600',
    },
  ];

  const kpis = [
    { label: t('portal.home.kpiPublished'), value: '148', hint: t('portal.home.kpiPublishedHint'), icon: 'ri-checkbox-circle-line' },
    { label: t('portal.home.kpiStock'), value: num(12896), hint: t('portal.home.kpiStockHint'), icon: 'ri-archive-2-line' },
    { label: t('portal.home.kpiReserved'), value: num(420), hint: t('portal.home.kpiReservedHint'), icon: 'ri-calendar-check-line' },
    { label: t('portal.home.kpiOrders'), value: '6', hint: t('portal.home.kpiOrdersHint'), icon: 'ri-shopping-bag-3-line' },
    { label: t('portal.home.kpiAwaiting'), value: 'AED 31,200', hint: t('portal.home.kpiAwaitingHint'), icon: 'ri-time-line' },
  ];

  const eventIcon: Record<string, string> = {
    order: 'ri-shopping-bag-3-line',
    payment: 'ri-wallet-3-line',
    reservation: 'ri-calendar-close-line',
    stock: 'ri-archive-2-line',
    ready: 'ri-truck-line',
    warranty: 'ri-shield-check-line',
  };

  const eventTone: Record<string, string> = {
    order: 'bg-primary-50 text-primary-700',
    payment: 'bg-emerald-50 text-emerald-700',
    reservation: 'bg-accent-50 text-accent-700',
    stock: 'bg-sky-50 text-sky-700',
    ready: 'bg-violet-50 text-violet-700',
    warranty: 'bg-rose-50 text-rose-700',
  };

  const eventLabel = (type: string, e: (typeof portalEvents)[number]) => {
    if (type === 'order') return t('portal.home.evOrder', { po: e.po, units: e.units });
    if (type === 'payment') return t('portal.home.evPayment', { amount: e.amount, invoice: e.invoice });
    if (type === 'reservation') return t('portal.home.evReservation', { no: e.no });
    if (type === 'stock') return t('portal.home.evStock', { sku: e.sku });
    if (type === 'ready') return t('portal.home.evReady', { po: e.po });
    return t('portal.home.evWarranty', { no: e.no, sku: e.sku });
  };

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 border-b border-background-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-700">
            {t('portal.home.eyebrow')}
          </p>
          <h1 className="text-[22px] font-semibold leading-7 text-foreground-950">{t('portal.home.title')}</h1>
          <p className="mt-1 max-w-2xl text-[13px] leading-5 text-foreground-500">{t('portal.home.description')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/portal/orders"
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-shopping-bag-3-line text-[14px]" />
            {t('portal.nav.orders')}
          </Link>
          <Link
            to="/portal/products"
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-archive-2-line text-[14px]" />
            {t('portal.home.kpiStock')}
          </Link>
        </div>
      </div>

      <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-foreground-400">
        {t('portal.home.attention')}
      </p>
      <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
        {actions.map((a) => (
          <Link
            key={a.to}
            to={a.to}
            className={`group flex flex-col justify-between rounded-lg border border-background-200 border-l-[3px] bg-background-50 p-4 transition-colors hover:bg-background-50 ${a.tone}`}
          >
            <div className="flex items-start gap-3">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${a.iconTone}`}>
                <i className={`${a.icon} text-[16px]`} />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold leading-5 text-foreground-950">{a.title}</p>
                <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{a.body}</p>
              </div>
            </div>
            <p className="mt-3 flex items-center gap-1 text-[11.5px] font-semibold text-primary-700">
              {a.cta}
              <i className="ri-arrow-right-line text-[12px] transition-transform group-hover:translate-x-0.5" />
            </p>
          </Link>
        ))}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{k.label}</p>
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary-50 text-primary-700">
                <i className={`${k.icon} text-[13px]`} />
              </span>
            </div>
            <p className="tnum mt-2.5 text-[22px] font-semibold leading-7 text-foreground-950">{k.value}</p>
            <p className="mt-0.5 text-[10.5px] leading-4 text-foreground-400">{k.hint}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel padded={false} className="xl:col-span-2">
          <div className="flex items-start justify-between gap-4 border-b border-background-200 px-5 py-3.5">
            <div>
              <h2 className="text-sm font-semibold text-foreground-950">{t('portal.home.timelineTitle')}</h2>
              <p className="mt-0.5 text-xs text-foreground-500">{t('portal.home.timelineSub')}</p>
            </div>
            <span className="rounded-full border border-background-200 px-2 py-0.5 text-[10px] font-medium text-foreground-500">
              {t('portal.demand.last90')}
            </span>
          </div>
          <ul className="divide-y divide-background-200/70">
            {portalEvents.map((e) => (
              <li key={e.id} className="flex items-start gap-3 px-5 py-3">
                <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${eventTone[e.type]}`}>
                  <i className={`${eventIcon[e.type]} text-[14px]`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] leading-5 text-foreground-800">{eventLabel(e.type, e)}</p>
                  <p className="tnum mt-0.5 text-[10.5px] text-foreground-400">{e.when}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="border-t border-background-200 px-5 py-2.5 text-[10.5px] text-foreground-400">
            {t('portal.home.note')}
          </div>
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel padded={false}>
            <div className="border-b border-background-200 px-5 py-3.5">
              <h2 className="text-sm font-semibold text-foreground-950">{t('portal.home.snapshotTitle')}</h2>
              <p className="mt-0.5 text-xs text-foreground-500">{t('portal.home.snapshotSub')}</p>
            </div>
            <dl className="divide-y divide-background-200/70 text-[12px]">
              {[
                { k: t('portal.home.contractLabel'), v: t('portal.home.contractValue') },
                { k: t('portal.home.discountLabel'), v: t('portal.home.discountValue') },
                { k: t('portal.home.reservationPeriodLabel'), v: t('portal.home.reservationPeriodValue') },
              ].map((row) => (
                <div key={row.k} className="flex items-center justify-between gap-3 px-5 py-2.5">
                  <dt className="text-foreground-500">{row.k}</dt>
                  <dd className="text-right font-medium text-foreground-900">{row.v}</dd>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 bg-primary-50/60 px-5 py-3">
                <dt className="text-foreground-700">{t('portal.home.nextPaymentLabel')}</dt>
                <dd className="tnum text-right text-[13px] font-semibold text-primary-800">
                  {t('portal.home.nextPaymentValue')}
                </dd>
              </div>
            </dl>
            <div className="border-t border-background-200 p-3">
              <Link
                to="/portal/finance"
                className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2 text-[11.5px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                {t('portal.nav.finance')}
                <i className="ri-arrow-right-line text-[12px] text-foreground-400" />
              </Link>
            </div>
          </Panel>

          <Panel>
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-foreground-400">
              {t('portal.home.managerLabel')}
            </p>
            <div className="mt-3 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-[11px] font-semibold text-secondary-900">
                LH
              </span>
              <div className="min-w-0">
                <p className="truncate text-[12.5px] font-semibold text-foreground-950">{t('portal.home.managerName')}</p>
                <p className="truncate text-[10.5px] text-foreground-500">{t('portal.home.managerRole')}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={`https://wa.me/${portalPartner.managerPhone.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded border border-primary-200 bg-primary-50 px-2 text-[10.5px] font-semibold text-primary-800 hover:bg-primary-100"
              >
                <i className="ri-whatsapp-line text-[12px]" />
                WhatsApp
              </a>
              <a
                href={`mailto:${portalPartner.managerEmail}`}
                className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded border border-background-300 bg-background-50 px-2 text-[10.5px] font-medium text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-mail-line text-[12px]" />
                {portalPartner.managerEmail}
              </a>
            </div>
          </Panel>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1 border-t border-background-200 pt-3 text-[10.5px] text-foreground-400 sm:flex-row sm:items-center sm:justify-between">
        <p>
          {t('portal.home.discountLabel')}: {portalPartner.discount}% · {t('portal.home.managerLabel')}:{' '}
          {portalPartner.manager}
        </p>
        <p className="tnum">{t('portal.home.snapshotSub')}</p>
      </div>
    </div>
  );
}